/**
 * Human title for a meeting on the credits page. Never a raw id.
 *
 * A subject that {@link displayMeetingTitle} keeps — a real title, or a legacy
 * auto-stamped one — is shown as-is. A blank subject uses the fallback title,
 * and the usage date is appended so two untitled meetings can be told apart.
 * Usage with no meeting, and the access-scoped "other meetings" bucket, keep
 * their own labels. Never a raw id.
 */

import { OTHER_MEETINGS_BUCKET_ID } from "@lexicanon/shared"
import type { I18n } from "@lingui/core"
import { msg } from "@lingui/core/macro"
import { formatDate } from "../../../lib/format-date"
import { displayMeetingTitle } from "../../../lib/meeting-title"

export interface IMeetingLabelRow {
  meetingId: string
  subject: string | null
  occurredAt?: string | null
}

export function creditMeetingTitle(i18n: I18n, row: IMeetingLabelRow): string {
  if (row.meetingId === OTHER_MEETINGS_BUCKET_ID) return i18n._(msg`Other meetings`)
  if (row.meetingId === "") return i18n._(msg`Unattributed`)

  const title = displayMeetingTitle(row.subject, null, i18n)
  const trimmed = row.subject?.trim() ?? ""
  if (trimmed.length > 0 && title === trimmed) return title

  const dateLabel = row.occurredAt ? formatDate(i18n, row.occurredAt, { year: false }) : ""
  return dateLabel ? `${title} · ${dateLabel}` : title
}
